/**
 * SQL fragments derived from a domain enum's literal set.
 *
 * `docs/ARCHITECTURE.md` §5 stores every enum as `TEXT` + a `CHECK` constraint, and
 * `docs/DECISIONS.md` ("Database — Cloudflare D1") explains why: SQLite has no native
 * enum type, so the constraint is the only thing keeping the column honest.
 *
 * The point of generating the constraint here rather than typing it into a migration is
 * that **the TypeScript union and the CHECK constraint come from one literal set and
 * cannot drift**. Adding a member to the union changes the constraint the next migration
 * emits; there is no second place to forget.
 *
 * This module produces text. It performs no I/O and knows nothing about Drizzle or D1.
 */

/**
 * Quotes a SQL string literal, doubling any embedded single quote.
 *
 * Domain enum members never contain a quote — they are lowercase identifiers — but the
 * escaping is real rather than assumed, because a helper that produced valid SQL only
 * for the inputs it happens to be given today is a SQL-injection footgun waiting for the
 * first tenant-supplied value someone passes it.
 */
export function quoteSqlLiteral(value: string): string {
  return `'${value.replaceAll("'", "''")}'`
}

/**
 * Quotes a SQL identifier, doubling any embedded double quote.
 */
export function quoteSqlIdentifier(identifier: string): string {
  if (identifier.length === 0) {
    throw new Error('A SQL identifier cannot be empty.')
  }
  return `"${identifier.replaceAll('"', '""')}"`
}

/**
 * The body of a `CHECK` constraint restricting `column` to `values`.
 *
 * ```ts
 * sqlEnumCheck('status', ['pending', 'active'])
 * // => `"status" IN ('pending', 'active')`
 * ```
 *
 * The caller wraps it: `CHECK (${sqlEnumCheck('status', values)})`. It is returned
 * unwrapped so a nullable column can compose it — `"status" IS NULL OR ...` — without
 * unwrapping something this function had already wrapped.
 */
export function sqlEnumCheck(column: string, values: readonly string[]): string {
  if (values.length === 0) {
    throw new Error(`Cannot build a CHECK constraint for "${column}" from an empty value set.`)
  }
  return `${quoteSqlIdentifier(column)} IN (${values.map(quoteSqlLiteral).join(', ')})`
}
